interface LaurelBadgeProps {
  label: string;
  sublabel: string;
  variant?: "laurel" | "star" | "shield" | "import-export";
  size?: "sm" | "md" | "lg" | "responsive";
}

const SIZE_MAP = {
  sm: "h-[56px] w-[56px]",
  md: "h-[72px] w-[72px]",
  lg: "h-[88px] w-[88px]",
  responsive:
    "h-[64px] w-[64px] sm:h-[76px] sm:w-[76px] md:h-[88px] md:w-[88px] xl:h-[96px] xl:w-[96px] 2xl:h-[108px] 2xl:w-[108px]",
};

const TEXT_SIZE_MAP = {
  sm: { label: "text-[5px]", sublabel: "text-[4px]" },
  md: { label: "text-[5.5px]", sublabel: "text-[4.5px]" },
  lg: { label: "text-[6.5px]", sublabel: "text-[5px]" },
  responsive: {
    label:
      "text-[5px] sm:text-[6px] md:text-[6.5px] xl:text-[7px] 2xl:text-[8px]",
    sublabel:
      "text-[4px] sm:text-[4.5px] md:text-[5px] xl:text-[5.5px] 2xl:text-[6px]",
  },
};

export function LaurelBadge({
  label,
  sublabel,
  variant = "import-export",
  size = "responsive",
}: LaurelBadgeProps) {
  const textSizes = TEXT_SIZE_MAP[size];

  return (
    <div
      className={`relative flex items-center justify-center ${SIZE_MAP[size]}`}
    >
      <svg
        viewBox="0 0 80 80"
        className="absolute inset-0 h-full w-full text-stone-900"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        aria-hidden="true"
      >
        {/* LAUREL VARIANT */}
        {variant === "laurel" && (
          <>
            <path d="M22 44 C 18 34, 20 22, 30 14" strokeLinecap="round" />
            <ellipse cx="21" cy="28" rx="3" ry="1.5" transform="rotate(-45 21 28)" fill="currentColor" />
            <ellipse cx="22" cy="34" rx="3" ry="1.5" transform="rotate(-35 22 34)" fill="currentColor" />
            <ellipse cx="23" cy="40" rx="3" ry="1.5" transform="rotate(-25 23 40)" fill="currentColor" />
            <ellipse cx="26" cy="46" rx="3" ry="1.5" transform="rotate(-15 26 46)" fill="currentColor" />

            <path d="M58 44 C 62 34, 60 22, 50 14" strokeLinecap="round" />
            <ellipse cx="59" cy="28" rx="3" ry="1.5" transform="rotate(45 59 28)" fill="currentColor" />
            <ellipse cx="58" cy="34" rx="3" ry="1.5" transform="rotate(35 58 34)" fill="currentColor" />
            <ellipse cx="57" cy="40" rx="3" ry="1.5" transform="rotate(25 57 40)" fill="currentColor" />
            <ellipse cx="54" cy="46" rx="3" ry="1.5" transform="rotate(15 54 46)" fill="currentColor" />

            <path
              d="M40 10 L41.8 14.2 L46.3 14.5 L42.9 17.4 L44.1 21.7 L40 19.3 L35.9 21.7 L37.1 17.4 L33.7 14.5 L38.2 14.2 Z"
              fill="currentColor"
              stroke="none"
            />
          </>
        )}

        {/* STAR VARIANT */}
        {variant === "star" && (
          <>
            <circle cx="40" cy="40" r="30" strokeWidth="1.3" />
            <path d="M18 55 L10 68 L20 65 L22 58 Z" fill="currentColor" stroke="none" />
            <path d="M62 55 L70 68 L60 65 L58 58 Z" fill="currentColor" stroke="none" />
            <path
              d="M40 22 L42.8 30.6 L52 30.6 L44.6 36 L47.4 44.6 L40 39.2 L32.6 44.6 L35.4 36 L28 30.6 L37.2 30.6 Z"
              fill="currentColor"
              stroke="none"
            />
            <circle cx="22" cy="40" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="58" cy="40" r="1.2" fill="currentColor" stroke="none" />
          </>
        )}

        {/* SHIELD VARIANT */}
        {variant === "shield" && (
          <>
            <circle cx="40" cy="40" r="30" strokeWidth="1.3" />
            <circle cx="40" cy="40" r="26" strokeWidth="0.5" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
              const rad = (deg * Math.PI) / 180;
              const x = 40 + Math.cos(rad) * 27;
              const y = 40 + Math.sin(rad) * 27;
              return (
                <ellipse
                  key={deg}
                  cx={x}
                  cy={y}
                  rx="2.5"
                  ry="1.2"
                  transform={`rotate(${deg} ${x} ${y})`}
                  fill="currentColor"
                  stroke="none"
                />
              );
            })}
          </>
        )}

        {/* IMPORT / EXPORT SPECIFIC VARIANT */}
        {variant === "import-export" && (
          <>
            {/* Outer Certificate Seal Ring */}
            <circle cx="40" cy="40" r="36" strokeWidth="1.2" />
            <circle cx="40" cy="40" r="33" strokeWidth="0.6" strokeDasharray="1.5 1.5" />

            {/* Globe Wireframe Body */}
            <circle cx="40" cy="40" r="23" strokeWidth="0.9" />
            {/* Equator & Latitudes */}
            <line x1="17" y1="40" x2="63" y2="40" strokeWidth="0.6" />
            <ellipse cx="40" cy="40" rx="23" ry="11" strokeWidth="0.6" />
            <ellipse cx="40" cy="40" rx="10" ry="23" strokeWidth="0.6" />

            {/* Import Arrow (Inbound - Left Side) */}
            <path
              d="M 10 40 L 19 40 M 15 36 L 19 40 L 15 44"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Export Arrow (Outbound - Right Side) */}
            <path
              d="M 61 40 L 70 40 M 66 36 L 70 40 L 66 44"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
      </svg>

      <div className="relative z-10 flex flex-col items-center justify-center text-center leading-none pointer-events-none px-1">
        <span
          className={`font-black uppercase tracking-[0.4px] text-stone-900 ${textSizes.label}`}
        >
          {label}
        </span>
        <span
          className={`mt-[2px] uppercase tracking-[0.3px] text-stone-800 ${textSizes.sublabel}`}
        >
          {sublabel}
        </span>
      </div>
    </div>
  );
}